import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, RefreshCw, Pause, XCircle, RotateCcw, Trash2 } from "lucide-react";
import api from "@/utils/api";
import { adminHeaders, apiError } from "@/components/admin/adminSession";

const STATUSES = ["all", "trial", "active", "paused", "cancelled"];
const STATUS_CLS = {
  trial: "bg-amber-500/20 text-amber-400",
  active: "bg-green-500/20 text-green-400",
  paused: "bg-sky-500/20 text-sky-300",
  cancelled: "bg-red-500/20 text-red-400",
};

const ACTIONS = {
  pause: { label: "Pause", icon: Pause, desc: "Freezes their premium access. Remaining days are restored when reactivated." },
  cancel: { label: "Cancel", icon: XCircle, desc: "Stops renewal. They keep access until their current period ends." },
  reactivate: { label: "Reactivate", icon: RotateCcw, desc: "Restores their membership (resumes a pause or reverses a cancellation)." },
  delete: { label: "Delete", icon: Trash2, desc: "Cancels billing and permanently deletes this account and all of its cycles and reminders. This cannot be undone." },
};

const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

function allowedActions(status) {
  if (status === "active" || status === "trial") return ["pause", "cancel", "delete"];
  if (status === "paused") return ["reactivate", "cancel", "delete"];
  if (status === "cancelled") return ["reactivate", "delete"];
  return ["delete"];
}

function endDate(s) {
  if (s.status === "paused") return `${s.days_remaining_at_pause ?? 0} days left`;
  return fmt(s.expires_at || s.trial_end);
}

function SubscriberRow({ s, onAction }) {
  return (
    <tr className="border-t border-zinc-800/50 hover:bg-zinc-900/30" data-testid={`subscriber-row-${s.id}`}>
      <td className="p-3 text-white">{s.name || "—"}</td>
      <td className="p-3 text-zinc-300">{s.email}</td>
      <td className="p-3"><Badge className={STATUS_CLS[s.status]} data-testid={`subscriber-status-${s.id}`}>{s.status}</Badge></td>
      <td className="p-3 text-zinc-400 capitalize">{s.plan || "—"}</td>
      <td className="p-3 text-zinc-400 text-xs">{endDate(s)}</td>
      <td className="p-3">
        <div className="flex gap-1 justify-end">
          {allowedActions(s.status).map((a) => {
            const Icon = ACTIONS[a].icon;
            return (
              <Button key={a} size="sm" variant="ghost" onClick={() => onAction(s, a)}
                className={a === "delete" ? "text-red-400 hover:text-red-300 hover:bg-red-500/10" : "text-zinc-300 hover:text-white"}
                data-testid={`subscriber-${a}-${s.id}`}>
                <Icon className="w-4 h-4 mr-1" />{ACTIONS[a].label}
              </Button>
            );
          })}
        </div>
      </td>
    </tr>
  );
}

function ConfirmAction({ pending, busy, onCancel, onConfirm }) {
  const a = pending ? ACTIONS[pending.action] : null;
  return (
    <AlertDialog open={!!pending} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent className="bg-zinc-950 border-zinc-800" data-testid="subscriber-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">{a?.label} {pending?.sub.email}?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">{a?.desc}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="subscriber-confirm-cancel">Keep as is</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={busy}
            className={pending?.action === "delete" ? "bg-red-600 hover:bg-red-500 text-white" : "btn-primary"}
            data-testid="subscriber-confirm-ok">
            Yes, {a?.label.toLowerCase()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function AdminSubscribersTab() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (status !== "all") params.set("status", status);
      if (search) params.set("search", search);
      const res = await api.get(`/admin/subscribers?${params}`, { headers: adminHeaders() });
      setRows(res.data.subscribers || []);
      setCounts(res.data.counts || {});
    } catch (e) { toast.error(apiError(e, "Failed to load subscribers")); }
    finally { setLoading(false); }
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    const { sub, action } = pending;
    setBusy(true);
    try {
      const res = action === "delete"
        ? await api.delete(`/admin/subscribers/${sub.id}`, { headers: adminHeaders() })
        : await api.post(`/admin/subscribers/${sub.id}/${action}`, {}, { headers: adminHeaders() });
      toast.success(res.data.message);
      setPending(null);
      load();
    } catch (e) { toast.error(apiError(e, "Action failed")); }
    finally { setBusy(false); }
  };

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-6" data-testid="admin-subscribers-tab">
      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`subscriber-filter-${s}`}
            className={`px-4 py-2 rounded-full text-sm capitalize transition-colors ${status === s ? "bg-cyan-500 text-black font-semibold" : "bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800"}`}>
            {s} <span className="opacity-70">({s === "all" ? total : counts[s] || 0})</span>
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-3 text-zinc-600" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or phone"
            className="pl-9 bg-zinc-900 border-zinc-800" data-testid="subscriber-search" />
        </div>
        <Button variant="outline" onClick={load} data-testid="subscriber-refresh"><RefreshCw className="w-4 h-4" /></Button>
      </div>
      <Card className="bg-zinc-950/50 border-zinc-800">
        <CardContent className="p-0">
          {loading ? <div className="p-12 flex justify-center"><div className="spinner" /></div>
            : rows.length === 0 ? <div className="p-12 text-center text-zinc-500" data-testid="subscribers-empty">No subscribers found.</div>
              : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="subscribers-table">
                    <thead className="bg-zinc-900/80 text-zinc-400 text-xs uppercase">
                      <tr>
                        <th className="text-left p-3">Name</th><th className="text-left p-3">Email</th>
                        <th className="text-left p-3">Status</th><th className="text-left p-3">Plan</th>
                        <th className="text-left p-3">Ends</th><th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>{rows.map((s) => <SubscriberRow key={s.id} s={s} onAction={(sub, action) => setPending({ sub, action })} />)}</tbody>
                  </table>
                </div>
              )}
        </CardContent>
      </Card>
      <ConfirmAction pending={pending} busy={busy} onCancel={() => setPending(null)} onConfirm={confirm} />
    </div>
  );
}
